import { useMemo } from 'react'

/**
 * useCitizenData
 *
 * Deriva datos para la vista "Ciudadano" a partir del contexto de cálculo existente.
 * No crea estado nuevo, no ejecuta el engine — solo transforma datos existentes
 * en la estructura que necesita la UI ciudadana.
 *
 * @param {Object} options
 * @param {Object}  options.contextoDictamen  — resultado de composeDictamenContext
 * @param {Object}  options.expediente        — datos del titular
 * @param {Object}  options.activeRules       — resultado de strategicEvaluator
 * @param {Array}   options.rows              — filas del historial laboral
 * @returns {Object} datos formateados para la vista ciudadano
 */
export default function useCitizenData({ contextoDictamen, expediente, activeRules, rows }) {
  return useMemo(() => {
    if (!contextoDictamen || !expediente) {
      return getDefaultValue()
    }

    const resumen = contextoDictamen.resumenEjecutivo || {}
    const hoy = new Date()

    // --- Datos básicos del titular ---
    const nombre = [expediente.nombre, expediente.apellido].filter(Boolean).join(' ')
    const sexo = (expediente.sexo || 'M').toUpperCase()
    const edadJubilatoria = sexo === 'F' ? 60 : 65
    const edad = calcularEdad(expediente.fechaNacimiento, hoy)

    // --- Años de aporte ---
    const aniosAporteTotal = activeRules?.caseMeta?.aporteAnios || 0
    const aniosAporteEntero = Math.round(aniosAporteTotal)
    const aniosRequeridos = sexo === 'F' ? 30 : 35
    const aniosFaltantes = Math.max(0, aniosRequeridos - aniosAporteEntero)
    const puedeJubilarse = edad >= edadJubilatoria && aniosAporteTotal >= aniosRequeridos

    // --- Datos financieros (desde el motor existente) ---
    const haberCalculado = resumen.haberBolsilloReajustado || 0
    const haberActual = resumen.haberActualAnses || 0
    const diferencial = resumen.diferencialMensual || 0
    const retroactivo = resumen.retroactivoTotal || 0

    // --- Gaps (períodos sin dato) ---
    const rowsList = Array.isArray(rows) ? rows : []
    const gapsTotal = rowsList.filter(r => r.requiresRevision).length
    const gapsPre94 = rowsList.filter(r =>
      r.requiresRevision && r.periodoKey < '1994-07'
    ).length
    const gapsPost94 = gapsTotal - gapsPre94

    // --- Salario promedio reciente (para estimación de escenarios) ---
    const rowsConMonto = rowsList.filter(r => r.monto > 0 && !r.missingInSource)
    const ultimos12 = rowsConMonto.slice(-12)
    const promedioReciente = ultimos12.length > 0
      ? ultimos12.reduce((s, r) => s + (r.monto || 0), 0) / ultimos12.length
      : 0

    // --- Escenarios what-if (estimaciones, no cálculos exactos del engine) ---
    const escenarios = buildEscenarios({
      haberCalculado,
      aniosFaltantes,
      aniosAporteTotal,
      edad,
      edadJubilatoria,
      puedeJubilarse,
      promedioReciente,
      sexo,
      expediente,
    })

    // --- Checklist de situación ---
    const checklist = buildChecklist({
      aniosAporteEntero,
      aniosFaltantes,
      aniosRequeridos,
      edad,
      edadJubilatoria,
      gapsPre94,
      gapsPost94,
      sexo,
    })

    return {
      nombre,
      edad,
      sexo,
      edadJubilatoria,
      aniosAporte: Math.round(aniosAporteTotal * 10) / 10,
      aniosFaltantes,
      puedeJubilarse,
      haberCalculado,
      haberActual,
      diferencial,
      retroactivo,
      gapsTotal,
      gapsPre94,
      checklist,
      escenarios,
    }
  }, [contextoDictamen, expediente, activeRules, rows])
}

/* ------------------------------------------------------------------ */
/*  Cálculos auxiliares                                                */
/* ------------------------------------------------------------------ */

function calcularEdad(fechaNacimiento, hoy) {
  if (!fechaNacimiento) return 0
  const nac = new Date(fechaNacimiento)
  if (isNaN(nac.getTime())) return 0
  let edad = hoy.getFullYear() - nac.getFullYear()
  const mes = hoy.getMonth() - nac.getMonth()
  if (mes < 0 || (mes === 0 && hoy.getDate() < nac.getDate())) {
    edad--
  }
  return Math.max(0, edad)
}

function buildEscenarios({
  haberCalculado, aniosFaltantes, aniosAporteTotal,
  edad, edadJubilatoria, puedeJubilarse, promedioReciente, sexo,
  expediente,
}) {
  const escenarios = []

  // 1. Jubilarte hoy
  escenarios.push({
    id: 'hoy',
    icon: '🎯',
    color: 'green',
    titulo: 'Jubilarte hoy',
    descripcion: 'Con los aportes que tenés, iniciando el trámite ahora mismo',
    haber: haberCalculado,
    etiquetaValor: `$${formatMoney(haberCalculado)}/mes`,
  })

  // 2. Esperar y seguir trabajando
  {
    // Estimación: ~5-7% por año adicional trabajado (heuristic)
    const aniosEspera = Math.max(1, Math.min(5, aniosFaltantes || 2))
    const factorEspera = 1 + aniosEspera * 0.06
    const haberEspera = Math.round(haberCalculado * factorEspera)
    const incremento = haberCalculado > 0
      ? Math.round((haberEspera - haberCalculado) / haberCalculado * 100)
      : 0

    escenarios.push({
      id: 'esperar',
      icon: '📈',
      color: 'blue',
      titulo: `Esperar y seguir trabajando (${aniosEspera} año${aniosEspera !== 1 ? 's' : ''})`,
      descripcion: 'Si trabajás más tiempo, sumás aportes y mejorás el haber',
      haber: haberEspera,
      incremento,
      etiquetaValor: `$${formatMoney(haberEspera)}/mes (+${incremento}%)`,
      esEstimado: true,
    })
  }

  // 3. Comprar años faltantes (moratoria)
  if (aniosFaltantes > 0) {
    // Estimación: ~2% por año comprado (menor que trabajar porque se compra a valores mínimos)
    const factorCompra = 1 + aniosFaltantes * 0.02
    const haberCompra = Math.round(haberCalculado * factorCompra)
    const incremento = haberCalculado > 0
      ? Math.round((haberCompra - haberCalculado) / haberCalculado * 100)
      : 0

    escenarios.push({
      id: 'comprar',
      icon: '🛒',
      color: 'orange',
      titulo: 'Comprar años faltantes (moratoria)',
      descripcion: `Completás los ${aniosFaltantes} año${aniosFaltantes !== 1 ? 's' : ''} que te faltan mediante la moratoria vigente (Ley 27.705)`,
      haber: haberCompra,
      incremento,
      etiquetaValor: `$${formatMoney(haberCompra)}/mes (+${incremento}%)`,
      esEstimado: true,
    })
  }

  // 4. Reajuste (si ya está jubilado)
  if (expediente?.tieneBeneficioPrevio) {
    escenarios.push({
      id: 'reajuste',
      icon: '🔍',
      color: 'red',
      titulo: 'Revisar si podés reajustar',
      descripcion: 'Ya estás jubilado pero creés que cobrás menos de lo que te corresponde',
      haber: null,
      etiquetaValor: 'Ver si aplica →',
      esEstimado: false,
    })
  }

  return escenarios
}

function buildChecklist({
  aniosAporteEntero, aniosFaltantes, aniosRequeridos,
  edad, edadJubilatoria, gapsPre94, gapsPost94, sexo,
}) {
  const items = []

  // Años de aportes
  if (aniosFaltantes > 0) {
    items.push({
      icon: 'pending',
      titulo: `Te faltan ${aniosFaltantes} año${aniosFaltantes !== 1 ? 's' : ''} de aportes para llegar a los ${aniosRequeridos} requeridos`,
      detalle: `Actualmente tenés ${aniosAporteEntero} años con aportes registrados. Necesitás ${aniosRequeridos} para la jubilación ordinaria${sexo === 'F' ? ' (mujeres)' : ''}.`,
    })
  } else {
    items.push({
      icon: 'ok',
      titulo: 'Completás los años de aportes requeridos',
      detalle: `Tenés ${aniosAporteEntero} años de aportes registrados, cumpliendo el mínimo de ${aniosRequeridos}.`,
    })
  }

  // Edad
  if (edad >= edadJubilatoria) {
    items.push({
      icon: 'ok',
      titulo: 'Tenés la edad suficiente para iniciar el trámite',
      detalle: `Con ${edad} años, cumplís el requisito de edad (${edadJubilatoria} años${sexo === 'F' ? ' para mujeres' : ''}).`,
    })
  } else {
    const aniosRestantes = edadJubilatoria - edad
    items.push({
      icon: aniosRestantes <= 5 ? 'pending' : 'missing',
      titulo: `Te faltan ${aniosRestantes} año${aniosRestantes !== 1 ? 's' : ''} para alcanzar la edad jubilatoria`,
      detalle: `Actualmente tenés ${edad} años. La edad mínima es ${edadJubilatoria} años${sexo === 'F' ? ' para mujeres' : ''}.`,
    })
  }

  // Gaps pre-94
  if (gapsPre94 > 0) {
    items.push({
      icon: 'missing',
      titulo: `Hay ${gapsPre94} períodos sin registro de sueldo en tu historial`,
      detalle: gapsPre94 > 20
        ? 'Son años anteriores a 1994 donde ANSES no tiene digitalizados tus salarios. No impide la jubilación, pero puede afectar el monto final. Un abogado previsional puede ayudarte a reconstruirlos.'
        : 'Son períodos sin salario registrado. No impide la jubilación, pero puede afectar el monto final.',
    })
  }

  // Gaps post-94
  if (gapsPost94 > 0) {
    items.push({
      icon: 'pending',
      titulo: `Hay ${gapsPost94} períodos recientes sin registro`,
      detalle: 'Son períodos sin actividad registrada después de 1994. Correspondería evaluar si hubo trabajo no registrado o si aplica moratoria.',
    })
  }

  return items
}

/* ------------------------------------------------------------------ */
/*  Utilities                                                          */
/* ------------------------------------------------------------------ */

function formatMoney(value) {
  if (typeof value !== 'number' || isNaN(value)) return '0'
  return Math.round(value).toLocaleString('es-AR')
}

function getDefaultValue() {
  return {
    nombre: '',
    edad: 0,
    sexo: 'M',
    edadJubilatoria: 65,
    aniosAporte: 0,
    aniosFaltantes: 0,
    puedeJubilarse: false,
    haberCalculado: 0,
    haberActual: 0,
    diferencial: 0,
    retroactivo: 0,
    gapsTotal: 0,
    gapsPre94: 0,
    checklist: [],
    escenarios: [],
  }
}
